import {inject, Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {map, tap} from 'rxjs/operators';
import {OpenStreetMapComponent} from '@components/openstreetmap/openstreetmap.component';
import {TileLayerService} from '@6grain/masofa-monolith-api-client/map';
import {TileLayerBaseGetQuery} from '@6grain/masofa-monolith-api-client/map/model/tileLayerBaseGetQuery';
import {WeatherDataType} from '@6grain/masofa-monolith-api-client/map/model/weatherDataType';
import {WeatherDataSource} from '@6grain/masofa-monolith-api-client/map/model/weatherDataSource';

const DATA_TYPE_MAP: Record<string, WeatherDataType> = {
  weather: WeatherDataType.NUMBER_3,
  normal: WeatherDataType.NUMBER_1,
  deviation: WeatherDataType.NUMBER_2
};

const DATA_SOURCE_MAP: Record<string, WeatherDataSource> = {
  era5: WeatherDataSource.NUMBER_1,
  ibm: WeatherDataSource.NUMBER_2,
  ugm: WeatherDataSource.NUMBER_3
};

@Injectable({ providedIn: 'root' })
export class TilesManager {
  private tileLayerService = inject(TileLayerService);
  private indexAvailableYear: Record<string, string[]> = {};
  private lastSelectedYear = 0;
  private mapInstance!: OpenStreetMapComponent;

  initialize(
    mapInstance: OpenStreetMapComponent,
    dataType: WeatherDataType | string | null | undefined,
    weatherDataSource: WeatherDataSource | string | null | undefined
  ): void {
    this.mapInstance = mapInstance;
    this.GetWeatherLayerDates(new Date().getFullYear(), dataType, weatherDataSource).subscribe();
  }

  loadTileLayers(filter: TileLayerBaseGetQuery = {}): Observable<any> {
    return this.tileLayerService.tilesTileLayerGetByQueryPost(filter);
  }

  private toWeatherDataType(dataType: WeatherDataType | string | null | undefined): WeatherDataType {
    if (dataType == null) {
      return WeatherDataType.NUMBER_3;
    }
    return typeof dataType === 'string'
      ? (DATA_TYPE_MAP[dataType] ?? WeatherDataType.NUMBER_3)
      : dataType;
  }

  private toWeatherDataSource(weatherDataSource: WeatherDataSource | string | null | undefined): WeatherDataSource {
    if (weatherDataSource == null) {
      return WeatherDataSource.NUMBER_1;
    }
    return typeof weatherDataSource === 'string'
      ? (DATA_SOURCE_MAP[weatherDataSource.toLowerCase()] ?? WeatherDataSource.NUMBER_1)
      : weatherDataSource;
  }

  private getIndexKey(year: number, dataType: WeatherDataType, weatherDataSource: WeatherDataSource): string {   
    return `${year}_${dataType}_${weatherDataSource}`;
  }

  getAllCachedDatesForType(
    dataType: WeatherDataType | string | null | undefined,
    weatherDataSource: WeatherDataSource | string | null | undefined
  ): Record<number, string[]> {
    const type = this.toWeatherDataType(dataType).toString();
    const ds = this.toWeatherDataSource(weatherDataSource).toString();
    return Object.entries(this.indexAvailableYear)
      .filter(([key]) => key.endsWith(`_${type}_${ds}`))
      .reduce((acc, [key, dates]) => {
        acc[parseInt(key.split('_')[0], 10)] = dates;
        return acc;
      }, {} as Record<number, string[]>);
  }

  /**
   * Get all available dates for a data type (flattened from all years)
   * @param dataType - The weather data type
   * @param weatherDataSource - The weather data source (era5, ibm, ugm)
   * @returns Array of all available dates across all cached years
   */
  getAllAvailableDatesByIndex(
    dataType: WeatherDataType | string | null | undefined,
    weatherDataSource: WeatherDataSource | string | null | undefined
  ): string[] {
    const type = this.toWeatherDataType(dataType).toString();
    const ds = this.toWeatherDataSource(weatherDataSource).toString();
    return Object.entries(this.indexAvailableYear)
      .filter(([key]) => {
        const parts = key.split('_');
        return parts.length >= 3 && parts[1] === type && parts[2] === String(ds);
      })
      .flatMap(([, dates]) => dates);
  }

  GetWeatherLayerDates(
    year: number,
    dataType: WeatherDataType | string | null | undefined,
    weatherDataSource: WeatherDataSource | string | null | undefined
  ): Observable<Record<number, string[]>> {
    const type = this.toWeatherDataType(dataType);
    const ds = this.toWeatherDataSource(weatherDataSource);
    const indexKey = this.getIndexKey(year, type, ds);
    this.lastSelectedYear = year;

    if (this.indexAvailableYear[indexKey]) {
      return of(this.getAllCachedDatesForType(dataType, weatherDataSource));
    }

    return this.tileLayerService.tilesTileLayerGetWeatherLayerDatesGet(year, type, ds).pipe(
      tap(dates => this.indexAvailableYear[indexKey] = dates),
      map(() => this.getAllCachedDatesForType(dataType, weatherDataSource))
    );
  }


  get getIndexAvailableYear() { return this.indexAvailableYear; }


  get getLastSelectedYear() { return this.lastSelectedYear; }
}
